import { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import config from "../config";
import { authApi } from "../apis/authApi";
import { setWebAuthTokenMirror } from "../utils/authTokenStorage";

export interface UserMspInfo {
  id: number;
  email: string;
  /** PSA technician / owner id stored for this Theo user — used with PSA ticket assignment. */
  psa_user_id?: string | null;
  role: string;
  msp_id: number;
  msp_sku: string;
  credit_billing_enabled: boolean;
  msp_type?: string;
  msp_name?: string;
}

interface UserMspCacheEntry {
  info: UserMspInfo | null;
  error: string | null;
  loaded: boolean;
  promise: Promise<UserMspInfo | null> | null;
}

const userMspCache = new Map<string, UserMspCacheEntry>();

const getCachedEntry = (email?: string) =>
  email ? userMspCache.get(email.toLowerCase()) : undefined;

export function useUserMsp() {
  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const requestEmail = isAuthenticated ? user?.email : undefined;
  const cacheEmail = requestEmail?.toLowerCase();
  const initialCacheEntry = getCachedEntry(cacheEmail);
  const [userMspInfo, setUserMspInfo] = useState<UserMspInfo | null>(
    initialCacheEntry?.loaded ? initialCacheEntry.info : null
  );
  const [isLoading, setIsLoading] = useState(
    Boolean(cacheEmail && !initialCacheEntry?.loaded)
  );
  const [error, setError] = useState<string | null>(
    initialCacheEntry?.loaded ? initialCacheEntry.error : null
  );

  const fetchUserMsp = useCallback(async () => {
    if (!isAuthenticated || !requestEmail || !cacheEmail) {
      setUserMspInfo(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const existing = getCachedEntry(cacheEmail);
    if (existing?.loaded) {
      setUserMspInfo(existing.info);
      setError(existing.error);
      setIsLoading(false);
      return existing.info;
    }

    if (existing?.promise) {
      setIsLoading(true);
      const cachedResult = await existing.promise;
      const latest = getCachedEntry(cacheEmail);
      setUserMspInfo(latest?.info ?? cachedResult);
      setError(latest?.error ?? null);
      setIsLoading(false);
      return cachedResult;
    }

    const cacheEntry: UserMspCacheEntry = {
      info: null,
      error: null,
      loaded: false,
      promise: null,
    };
    userMspCache.set(cacheEmail, cacheEntry);

    setIsLoading(true);
    setError(null);

    try {
      const tokenResult: any = await getAccessTokenSilently({
        authorizationParams: {
          audience: config.audience || "",
        },
      } as any);
      const token =
        typeof tokenResult === "string"
          ? tokenResult
          : tokenResult?.access_token || "";
      if (token) {
        setWebAuthTokenMirror(token);
      }
    } catch {
      // Let HttpClient surface the actual API/auth error below.
    }

    cacheEntry.promise = authApi
      .getUserMsp(requestEmail)
      .then((response) => {
        cacheEntry.info = response.user;
        cacheEntry.error = null;
        cacheEntry.loaded = true;
        cacheEntry.promise = null;
        return response.user;
      })
      .catch((err: any) => {
        const message = err.message || "Failed to fetch user MSP information";
        cacheEntry.info = null;
        cacheEntry.error = message;
        cacheEntry.loaded = true;
        cacheEntry.promise = null;
        return null;
      });
    const responseUser = await cacheEntry.promise;
    setUserMspInfo(responseUser);
    setError(cacheEntry.error);
    setIsLoading(false);
    return responseUser;
  }, [cacheEmail, getAccessTokenSilently, isAuthenticated, requestEmail]);

  useEffect(() => {
    fetchUserMsp();
  }, [fetchUserMsp]);

  const cacheEntry = getCachedEntry(cacheEmail);
  const effectiveLoading = Boolean(
    authLoading || (cacheEmail && !cacheEntry?.loaded && (isLoading || cacheEntry?.promise || !cacheEntry))
  );
  const shouldShowGuides = userMspInfo?.msp_sku !== "assistant";

  return {
    userMspInfo,
    isLoading: effectiveLoading,
    error,
    shouldShowGuides,
    refetch: fetchUserMsp,
  };
}
